import { useState } from "react";

interface Props {
  message: string;
  detail?: string;
  confirmLabel?: string;
  danger?: boolean;
  /** When provided, user must type this exact string to enable the confirm button */
  typeToConfirm?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  message, detail, confirmLabel = "Confirm", danger = true,
  typeToConfirm, onConfirm, onCancel,
}: Props) {
  const [typed, setTyped] = useState("");
  const canConfirm = !typeToConfirm || typed === typeToConfirm;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(5,5,15,0.82)", backdropFilter: "blur(16px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div style={{
        background: "linear-gradient(160deg,#2a1f5c,#08060f)",
        border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20,
        padding: "28px 24px", maxWidth: 400, width: "100%",
        display: "flex", flexDirection: "column", gap: 18,
        animation: "sparkle-pop 0.18s ease",
      }}>
        <div>
          <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16, color: "#f0f0ff", margin: "0 0 6px", lineHeight: 1.4 }}>
            {message}
          </p>
          {detail && (
            <p style={{ fontSize: 13, color: "rgba(240,240,255,0.5)", margin: 0, lineHeight: 1.5 }}>{detail}</p>
          )}
        </div>

        {typeToConfirm && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 12, fontFamily: "var(--font-mono)", letterSpacing: "0.08em", color: "rgba(240,240,255,0.5)", textTransform: "uppercase" }}>
              Type <strong style={{ color: "#f87171", fontStyle: "italic" }}>{typeToConfirm}</strong> to confirm
            </label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canConfirm) onConfirm(); if (e.key === "Escape") onCancel(); }}
              style={{
                padding: "10px 14px", borderRadius: 10, fontSize: 14, fontWeight: 700,
                fontFamily: "var(--font-display)", background: "rgba(0,0,0,0.4)",
                border: `1px solid ${typed === typeToConfirm ? "rgba(248,113,113,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
                transition: "border-color 0.15s",
              }}
              placeholder={typeToConfirm}
            />
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{ padding: "9px 18px", borderRadius: 10, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(240,240,255,0.7)", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{
              padding: "9px 18px", borderRadius: 10, cursor: canConfirm ? "pointer" : "not-allowed",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14,
              opacity: canConfirm ? 1 : 0.4,
              transition: "opacity 0.15s",
              ...(danger
                ? { background: "rgba(239,68,68,0.18)", border: "1px solid rgba(239,68,68,0.4)", color: "#f87171" }
                : { background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.35)", color: "#fbbf24" }),
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
